import { h } from 'preact';
import Store from 'store/store';

import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { shouldShowNavigationLink } from 'utils/navigationHelpers';
import { canUser } from 'utils/roles';
import { getCurrentHubDomain } from 'utils/studioConfig';
import { joinClasses, sortObjectByOrder } from 'utils/utils';

import Button from 'components/button/button';
import MovieEditor from 'components/editor/movies/movieEditor';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import ToggleField from 'components/editor/settings/toggleField';
import NavDownIcon from 'components/icons/navDown';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';

import s from 'components/editor/movies/movieHeader/movieHeader.sss';

export default class MovieHeader extends MovieEditor {
	s = s;

	constructor(props) {
		super(props);

		this.state.visibleCount = null;

		this.navRef = null;
		this.resizeObserver = null;

		this.renderLink = this.renderLink.bind(this);
		this.onLinksSort = this.onLinksSort.bind(this);
		this.addNavItem = this.addNavItem.bind(this);
		this.onResetClick = this.onResetClick.bind(this);
		this.ensureOverride = this.ensureOverride.bind(this);
		this.deleteNavItem = this.deleteNavItem.bind(this);
		this.resetNavItem = this.resetNavItem.bind(this);
		this.calculateVisibleItems = this.calculateVisibleItems.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();

		// Ensure any new default links are added to the store when component mounts (if not relying on defaults)
		if (this.props.data?.content?.header?.links) {
			this.ensureOverride();
		}

		// Set up ResizeObserver to watch nav container
		if (this.navRef) {
			this.resizeObserver = new ResizeObserver(() => this.calculateVisibleItems());
			this.resizeObserver.observe(this.navRef);
			this.calculateVisibleItems();
		}
	}

	componentDidUpdate(prevProps) {
		super.componentDidUpdate?.(prevProps);

		// Recalculate when settings panel opens/closes
		if (!!prevProps.activeElement !== !!this.props.activeElement) {
			this.calculateVisibleItems();
		}
	}

	componentWillUnmount() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		if (this.onStoreUpdate) {
			Store.off('update', this.onStoreUpdate);
		}
	}

	// ===============
	// HELPERS
	// ===============

	calculateVisibleItems() {
		if (!this.navRef) return;

		const navWidth = this.navRef.clientWidth;

		// Account for "Home" + "More" button + gaps
		const homeWidth = 60; // estimate
		const moreButtonWidth = 80; // estimate
		const gapWidth = 32;
		const separatorWidth = 17; // the ::before separator

		// Available width for nav items
		let availableWidth = navWidth - homeWidth - moreButtonWidth - separatorWidth - (gapWidth * 2);

		// Estimate item width (could measure actual DOM elements for precision)
		const avgItemWidth = 80; // less conservative to fit more items

		const maxByWidth = Math.floor(availableWidth / (avgItemWidth + gapWidth));

		// Use the minimum of static logic and width-based calculation
		const isSettingsPanelOpen = !!this.props.activeElement;
		// When sidebar is open, less space = fewer items. When closed, more space = more items.
		const staticMax = isSettingsPanelOpen ? 4 : 5;

		const finalMax = Math.min(staticMax, Math.max(2, maxByWidth)); // at least 2 items

		// Only update state if the value changed
		if (this.state.visibleCount !== finalMax) {
			this.setState({ visibleCount: finalMax });
		}
	}

	// MUST OVERRIDE
	getDefaultLinks() {
		return {};
	}

	getLinks() {
		return this.props.data?.content?.header?.links || this.getDefaultLinks();
	}

	getAvailableLinks() {
		return sortObjectByOrder(this.getLinks())
			.filter(link => shouldShowNavigationLink(link, this.movie));
	}

	getVisibleLinks() {
		const links = this.getAvailableLinks().filter(link => !link.hidden);

		// When sidebar is open, less space = fewer items. When closed, more space = more items.
		const isSettingsPanelOpen = !!this.props.activeElement;
		const staticMaxLinks = isSettingsPanelOpen ? 4 : 5;

		// Use dynamic count if available, otherwise fallback to static
		const maxLinks = this.state.visibleCount ?? staticMaxLinks;

		const primaryLinks = links.slice(0, maxLinks - 1);
		const secondaryLinks = links.slice(maxLinks - 1);

		return { primaryLinks, secondaryLinks };
	}

	ensureOverride() {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/header/links`, {
			...this.getDefaultLinks(),
			...this.getLinks(),
		});
	}

	onLinksSort(e) {
		this.ensureOverride();

		// Calculate offset to account for permanent links that are not included in the sortable list
		const offset = this.getAvailableLinks().filter(link => link.permanent).length;

		// Get all link IDs from the sortable container
		const sortedLinks = [...e.target.querySelectorAll('[data-link]')].map((el) => el.dataset.link);

		// Reorder the array using splice
		sortedLinks.splice(e.to + offset, 0, sortedLinks.splice(e.from + offset, 1)[0]);

		// Update order for each link
		sortedLinks.forEach((id, index) => {
			const path = `${this.props.dataRoot}/content/header/links/${id}/order`;
			jsonPointer.setDeep(Store.get().data, path, index);
		});
	}

	addNavItem() {
		this.ensureOverride();

		const id = crypto.randomUUID();

		// Calculate max order across both regular links and custom pages
		const allOrders = Object.values(this.getLinks()).map(link => link.order);
		const order = Math.max(...allOrders) + 1;

		const newLink = { id, name: 'Nav Item', destination: null, order };
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/header/links/${id}`, newLink);
	}

	async onResetClick() {
		const ok = await confirm(
			'This will reset all navigation items to their defaults.',
			{ title: 'Reset all navigation items?', button: 'Reset', danger: true }
		);
		if (!ok) return;

		jsonPointer.removeDeep(Store.get().data, `${this.props.dataRoot}/content/header/links`);
	}

	async deleteNavItem(e) {
		const id = e.currentTarget.closest('[data-id]')?.dataset?.id;
		if (!id) return;

		const ok = await confirm(
			`This will delete the navigation item from the navigation.`,
			{
				title: 'Delete navigation item?',
				danger: true,
				button: 'Delete',
			}
		);
		if (!ok) return;

		jsonPointer.removeDeep(Store.get().data, `${this.props.dataRoot}/content/header/links/${id}`);

		this.props.select('/header');
	}
	
	async resetNavItem(e) {
		const id = e.currentTarget.closest('[data-id]')?.dataset?.id;
		if (!id) return;

		const ok = await confirm('This will reset the navigation item to its default.', {
			title: 'Reset navigation item?',
			danger: true,
			button: 'Reset',
		});
		if (!ok) return;

		const defaultLink = this.getDefaultLinks()[id];
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/header/links/${id}`, defaultLink);

		this.props.select('/header');
	}

	// ===============
	// VISUAL
	// ===============

	// MUST OVERRIDE
	renderLogo() {}

	renderLink(link) {
		return (
			<SelectableSection section={`/header/links/${link.id}`} {...this.props} class={this.s.navItem} key={link.id}>
				{link.name}
			</SelectableSection>
		);
	}

	renderLinks() {
		const { primaryLinks, secondaryLinks } = this.getVisibleLinks();
		const showMore = secondaryLinks?.length > 0;

		return (
			<nav class={this.s.nav} ref={ref => this.navRef = ref}>
				{primaryLinks.map(this.renderLink)}
				{showMore && (
					<div class={this.s.moreExpander}>
						<button class={this.s.moreButton}>
							<span>{this.text('$NAV_MORE', 'More')}</span>
							<NavDownIcon class={this.s.moreIcon} />
						</button>
						<div class={this.s.moreExpandedMenu}>
							{secondaryLinks.map(link => this.renderLink(link, { showLinkOutIcon: true }))}
						</div>
					</div>
				)}
			</nav>
		);
	}

	renderVisual() {
		return (
			<SelectableSection section="/header" {...this.props} class={joinClasses(this.s.wrapper, this.props.class)}>
				<div class={this.s.main}>
					<h1>
						{this.renderLogo()}
					</h1>
				</div>
				<modal class={this.s.drawer}>
					{this.renderLinks()}
				</modal>
			</SelectableSection>
		);
	}

	// ==============
	// SETTINGS
	// ==============

	renderLinkListItem(link) {
		return (
			<div class={this.s.item} data-link={link.id}>
				<div class={this.s.label}>
					<span class={this.s.name}>{link.name}</span>
					{link.hidden && (<span class={this.s.hidden}>(hidden)</span>)}
				</div>
				<button class={this.s.edit} onClick={() => this.props.select(`/header/links/${link.id}`)}>
					Edit
				</button>
			</div>
		);
	}

	renderRootSettings() {
		const readonly = !canUser('write');

		// Check if there are actual differences from defaults
		const hasOverride = JSON.stringify(this.getLinks()) !== JSON.stringify(this.getDefaultLinks());

		// Use getAvailableLinks() to ensure sidebar shows same links as top nav
		// This guarantees both use identical filtering logic
		const allLinks = this.getAvailableLinks();
		const permenantLinks = allLinks.filter(link => link.permanent);
		const sortableLinks = allLinks.filter(link => !link.permanent);
		
		return (
			<div class={this.s.headerSettings}>
				<Sortable class={this.s.list} onChange={this.onLinksSort} disabled={readonly}>
					{permenantLinks.map(link => this.renderLinkListItem(link))}
					<hr />
					{sortableLinks.map((link) => (
						<SortableItem class={this.s.itemWrapper} key={link.id}>
							<Handle class={this.s.handle} />
							{this.renderLinkListItem(link)}
						</SortableItem>
					))}
				</Sortable>
				<div class={this.s.buttonRow}>
					<Button dark primary icon="plus" disabled={readonly} onClick={this.addNavItem}>
						Add item
					</Button>
					<Button dark text danger disabled={readonly || !hasOverride} onClick={this.onResetClick}>
						Reset all
					</Button>
				</div>
			</div>
		);
	}

	renderLinkSettings(id) {
		const readonly = !canUser('write');

		const link = this.getLinks()[id];
		const isPermentant = link.permanent;
		const isDefault = link.permanent || link.default;

		const defaultItem = isDefault && this.getDefaultLinks()[id];
		const isEdited = isDefault && (
			link.name !== defaultItem.name ||
			link.destination !== defaultItem.destination ||
			!!link.hidden !== !!defaultItem.hidden
		);

		return (
			<div class={this.s.itemSettings} data-id={id}>
				{/* Hide toggle - only show for default items that are not permanent */}
				{isDefault && !isPermentant && (
					<ToggleField
						dark
						checked={link.hidden || false}
						disabled={readonly}
						key="toggle"
						onInput={this.ensureOverride}
						path={`${this.props.dataRoot}/content/header/links/${id}/hidden`}
					>
						Hide from navigation
					</ToggleField>
				)}

				{/* Label field */}
				<SimpleField
					dark
					label="Label"
					path={`${this.props.dataRoot}/content/header/links/${id}/name`}
					defaultValue={link?.name}
					onInput={this.ensureOverride}
					disabled={readonly}
				/>

				{/* Target field - readonly for default items */}
				<SimpleField
					dark
					label="Target"
					type="url"
					path={`${this.props.dataRoot}/content/header/links/${id}/destination`}
					defaultValue={link?.destination}
					placeholder={`https://${getCurrentHubDomain()}`}
					onInput={this.ensureOverride}
					disabled={readonly || isDefault}
					readonly={readonly || isDefault}
				/>

				{/* Action buttons */}
				<div class={this.s.buttonRow}>
					{isDefault && (
						<Button dark text danger disabled={readonly || !isEdited} onClick={this.resetNavItem}>
							Reset item
						</Button>
					)}
					{!isDefault && (
						<Button dark text danger disabled={readonly} onClick={this.deleteNavItem}>
							Delete item
						</Button>
					)}
				</div>
			</div>
		);
	}

	renderSettings() {
		const nav = [{ label: 'Navigation', path: '/header' }];
		let content = this.renderRootSettings();

		const activeElement = this.props.activeElement;

		if (activeElement?.startsWith('/header/links/')) {
			const id = activeElement.split('/').pop();
			const name = this.getLinks()?.[id]?.name || 'Item';
			nav.push({ label: name, path: `/header/links/${id}` });
			content = this.renderLinkSettings(id);
		}

		return (
			<div class={this.s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}